import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Play, Calendar, Leaf, Eye, Film, Clock, Map, BookOpen } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { API, BACKEND_URL, resolveImg } from '@/config';

const CATEGORY_LABELS = {
  nasca: 'Líneas de Nasca',
  palpa: 'Líneas de Palpa',
  museum: 'Museo Virtual',
  archaeological: 'Sitios Arqueológicos',
  nature: 'Atractivos Naturales',
  other: 'Otros'
};

const LANGUAGE_BADGES = {
  es: '🇪🇸 ES',
  en: '🇺🇸 EN',
  ja: '🇯🇵 JA',
  pt: '🇵🇹 PT',
  zh: '🇨🇳 ZH',
  it: '🇮🇹 IT'
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [showcaseVideos, setShowcaseVideos] = useState([]);
  const [loadingShowcase, setLoadingShowcase] = useState(true);

  useEffect(() => {
    axios.get(`${API}/videos/showcase`)
      .then((res) => setShowcaseVideos(res.data || []))
      .catch(() => setShowcaseVideos([]))
      .finally(() => setLoadingShowcase(false));
  }, []);

  // Group videos by category, preserving insertion order
  const videosByCategory = showcaseVideos.reduce((acc, v) => {
    const key = v.category || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(v);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(/astronauta-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 hero-gradient"></div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="mb-8 flex justify-center">
            <img 
              src="/logo.png" 
              alt="Nasca360 Logo" 
              className="h-32 sm:h-40 lg:h-48 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          <h1 
            data-testid="hero-title"
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-2xl"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            nasca360.com
          </h1>
          <p 
            data-testid="hero-subtitle"
            className="text-xl sm:text-2xl text-white/90 mb-8 max-w-3xl mx-auto"
          >
            {t('landing.heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="explore-button"
              onClick={() => navigate('/gallery')}
              className="btn-peru text-lg px-8 py-6 rounded-full shadow-2xl hover:scale-105"
            >
              <Eye className="mr-2" />
              {t('landing.exploreBtn')}
            </Button>
            {!user && (
              <Button
                data-testid="login-button"
                onClick={() => navigate('/auth/login')}
                variant="outline"
                className="text-lg px-8 py-6 rounded-full bg-white/90 hover:bg-white border-2 border-amber-700 text-amber-900 hover:scale-105"
              >
                {t('nav.login')}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-16 text-amber-900">
            {t('landing.featuresTitle')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div data-testid="feature-card-explore" className="card-peru p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-amber-900">{t('landing.feature1Title')}</h3>
              <p className="text-gray-700">
                {t('landing.feature1Desc')}
              </p>
            </div>

            <div data-testid="feature-card-map3d" className="card-peru p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Map className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-amber-900">{t('landing.feature2Title')}</h3>
              <p className="text-gray-700">
                {t('landing.feature2Desc')}
              </p>
            </div>

            <div data-testid="feature-card-blog" className="card-peru p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-amber-900">{t('landing.feature3Title')}</h3>
              <p className="text-gray-700">
                {t('landing.feature3Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contenido Showcase Section */}
      <section data-testid="contenido-section" className="py-24 px-4 bg-gradient-to-b from-transparent to-amber-50/40">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Film className="w-4 h-4" />
              {t('landing.showcaseBadge')}
            </div>
            <h2
              className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('landing.showcaseTitle')}
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              {t('landing.showcaseSubtitle')}
            </p>
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-amber-400"></div>
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-amber-400"></div>
            </div>
          </div>

          {loadingShowcase ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : showcaseVideos.length === 0 ? (
            <div className="text-center py-16 bg-white/60 backdrop-blur rounded-3xl border-2 border-amber-200">
              <Film className="w-12 h-12 mx-auto text-amber-400 mb-3" />
              <p className="text-amber-900 font-medium">{t('landing.showcaseEmpty')}</p>
            </div>
          ) : (
            <div className="space-y-14" data-testid="contenido-grid">
              {Object.keys(videosByCategory).map((catKey) => (
                <div key={catKey} data-testid={`category-block-${catKey}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <h3
                      className="text-2xl sm:text-3xl font-bold text-amber-900"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {t(`categories.${catKey}`, { defaultValue: CATEGORY_LABELS[catKey] || catKey })}
                    </h3>
                    <span className="text-sm text-amber-700/70 bg-amber-100 px-3 py-1 rounded-full">
                      {videosByCategory[catKey].length} {videosByCategory[catKey].length === 1 ? t('landing.experience') : t('landing.experiences')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videosByCategory[catKey].map((video) => (
                      <div
                        key={video.id}
                        data-testid={`showcase-card-${video.id}`}
                        className="group bg-white rounded-2xl overflow-hidden border-2 border-amber-100 hover:border-amber-300 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      >
                        {/* Cover Image */}
                        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
                          {video.thumbnail_url ? (
                            <img
                              src={resolveImg(video.thumbnail_url)}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-12 h-12 text-amber-400" />
                            </div>
                          )}
                          {video.duration && (
                            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-black/70 backdrop-blur text-white text-xs px-2.5 py-1 rounded-full">
                              <Clock size={11} />
                              {video.duration}
                            </div>
                          )}
                          <div className="absolute top-3 right-3 bg-amber-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-md">
                            {t(`categories.${video.category}`, { defaultValue: CATEGORY_LABELS[video.category] || video.category })}
                          </div>
                        </div>
                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className="text-lg font-bold text-amber-900 line-clamp-2"
                              style={{ fontFamily: 'Playfair Display, serif' }}
                            >
                              {video.title}
                            </h4>
                            {video.language && (
                              <span className="shrink-0 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                                {LANGUAGE_BADGES[video.language] || video.language.toUpperCase()}
                              </span>
                            )}
                          </div>
                          {video.description && (
                            <p className="text-gray-600 text-sm mt-2 line-clamp-3">
                              {video.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Map 3D CTA */}
      <section data-testid="map-cta" className="py-24 px-4 bg-gradient-to-r from-amber-100 to-orange-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-amber-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Explora el Mapa Interactivo 3D
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Navega virtualmente sobre los geoglifos de Nasca y Palpa y descubre datos históricos exclusivos de cada figura arqueológica.
          </p>
          <Button
            data-testid="map-cta-button"
            onClick={() => navigate('/map3d')}
            className="btn-peru text-lg px-10 py-6 rounded-full hover:scale-105"
          >
            Ver Mapa 3D
          </Button>
        </div>
      </section>

      {/* Impact Section */}
      <section data-testid="impact-section" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-3xl p-12">
            <div className="flex items-center justify-center mb-8">
              <Leaf className="w-12 h-12 text-green-700 mr-4" />
              <h2 className="text-4xl font-bold text-amber-900">{t('landing.impactTitle')}</h2>
            </div>
            <p className="text-xl text-gray-700 text-center max-w-3xl mx-auto">
              {t('landing.impactDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-amber-900 to-orange-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-4 flex justify-center">
            <img 
              src="/logo.png" 
              alt="Nasca360 Logo" 
              className="h-16 w-auto object-contain opacity-90"
            />
          </div>
          <h3 className="text-2xl font-bold mb-4">nasca360.com</h3>
          <p className="text-amber-200 mb-4">
            Preservando y compartiendo el patrimonio cultural del Perú
          </p>
          <p className="text-amber-300 text-sm">
            © 2026 nasca360.com. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;